import { useState, useEffect, useCallback, useMemo } from 'react';
import type { HeatmapLeavesBlock, HeatmapBarSegment, DateRange } from './types';
import { LEAD_ROLES } from './types';
import { getVisibleHeatmapTeamMembers, mergeOverlappingPeriods } from './helpers';
import type { PlannedTeamLeaveType, ProjectTeamMemberDto } from 'domain/shared/newProject';
import { useDeliveryApi } from 'infrastructure/api';
import { useEmployeeNamesByAliases } from './useEmployeeNamesByAliases';
import { buildEmployeeFullName, buildEmployeeLastNameSortKey, type EmployeeNamesByAlias } from './employeeName';
import { LEAVE_TYPES, LEAVE_TYPES_ALIASES } from '../leave';

const isLeadRole = (role: string | null | undefined): boolean => {
  if (!role || typeof role !== 'string') return false;
  const normalized = role.trim();
  return Object.values(LEAD_ROLES).includes(normalized as (typeof LEAD_ROLES)[keyof typeof LEAD_ROLES]);
};

const getMemberLeaveBarColor = (member: { role?: string | null }): string =>
  isLeadRole(member.role) ? 'error.main' : 'border.medium';

interface UseHeatmapLeavesParams {
  project: {
    id: string | number;
    team?: { members?: ProjectTeamMemberDto[] };
    teamPlannedLeaves?: PlannedTeamLeaveType[];
  } | null;
  projectDateRange: DateRange | null;
  selectedEmployeeAliases?: string[];
}

interface UseHeatmapLeavesResult {
  leavesBlock: HeatmapLeavesBlock;
  isLoadingLeaves: boolean;
}

type ProjectTeamLeave = {
  endDate: string;
  startDate: string;
  id: number;
  projectId: string | number;
  employeeAlias: string;
  type: keyof typeof LEAVE_TYPES_ALIASES;
};

function getEmployeeAliasFromLeave(leave: ProjectTeamLeave): string {
  return leave.employeeAlias;
}

function groupLeavesByEmployeeAlias(leaves: ProjectTeamLeave[]): Record<string, ProjectTeamLeave[]> {
  const grouped: Record<string, ProjectTeamLeave[]> = {};
  for (const leave of leaves) {
    const key = getEmployeeAliasFromLeave(leave);
    (grouped[key] ??= []).push(leave);
  }
  return grouped;
}

export const groupPlannedLeavesByEmployeeAlias = (
  plannedLeaves: PlannedTeamLeaveType[],
): Record<string, PlannedTeamLeaveType[]> => {
  const grouped: Record<string, PlannedTeamLeaveType[]> = {};

  for (const leave of plannedLeaves) {
    const key = leave.employeeAlias;
    (grouped[key] ??= []).push(leave);
  }

  return grouped;
};

const filterMembersBySelection = (
  members: ProjectTeamMemberDto[],
  selectedAliases?: string[],
): ProjectTeamMemberDto[] => {
  if (!selectedAliases?.length) return members;
  const selected = new Set(selectedAliases);
  return members.filter(m => selected.has(m.employeeAlias));
};

const sortMembersByLastName = (
  members: ProjectTeamMemberDto[],
  namesByAlias: EmployeeNamesByAlias,
): ProjectTeamMemberDto[] =>
  [...members].sort((a, b) =>
    buildEmployeeLastNameSortKey(a.employeeAlias ?? '', namesByAlias[a.employeeAlias ?? '']).localeCompare(
      buildEmployeeLastNameSortKey(b.employeeAlias ?? '', namesByAlias[b.employeeAlias ?? '']),
    ),
  );

function buildLeavesBlock(
  members: ProjectTeamMemberDto[],
  leavesByEmployeeAlias: Record<string, ProjectTeamLeave[]>,
  plannedLeavesByEmployeeAlias: Record<string, PlannedTeamLeaveType[]>,
  namesByAlias: EmployeeNamesByAlias,
  selectedEmployeeAliases?: string[],
): HeatmapLeavesBlock {
  const visibleMembers = sortMembersByLastName(
    filterMembersBySelection(getVisibleHeatmapTeamMembers(members), selectedEmployeeAliases),
    namesByAlias,
  );
  const rows = visibleMembers.map(member => {
    const memberLeaves = leavesByEmployeeAlias[member.employeeAlias!] ?? [];
    const memberPlannedLeaves = plannedLeavesByEmployeeAlias[member.employeeAlias!] ?? [];
    const barColor = getMemberLeaveBarColor(member);
    const approvedBars = memberLeaves.map(leave => ({
      id: `approved_${String(leave.id)}`,
      startDate: leave.startDate,
      endDate: leave.endDate,
      color: barColor,
      label: LEAVE_TYPES[leave.type] ?? leave.type,
    }));
    const plannedBars = memberPlannedLeaves.map(leave => ({
      id: `planned_${String(leave.id)}`,
      startDate: leave.startDate,
      endDate: leave.endDate,
      color: 'background.primary',
      label: `Planned leave${leave.comment ? `: ${leave.comment}` : ''}`,
    }));
    // Keep planned leaves visually below approved leaves when periods overlap.
    const bars = [...plannedBars, ...approvedBars];
    return {
      id: `leave_${member.employeeAlias}`,
      label: buildEmployeeFullName(member.employeeAlias!, namesByAlias[member.employeeAlias!]),
      linkId: member.employeeAlias!,
      bars,
    };
  });

  const allPeriods = rows.flatMap(row => row.bars.map(b => ({ startDate: b.startDate, endDate: b.endDate })));
  const mergedPeriods = mergeOverlappingPeriods(allPeriods);
  const summaryBars: HeatmapBarSegment[] = mergedPeriods.map((range, index) => ({
    id: `summary_${index}`,
    startDate: range.startDate,
    endDate: range.endDate,
    color: 'warning.main',
  }));

  return { rows, summaryBars };
}

export function useHeatmapLeaves({
  project,
  projectDateRange,
  selectedEmployeeAliases,
}: UseHeatmapLeavesParams): UseHeatmapLeavesResult {
  const { sendGetRequest } = useDeliveryApi();
  const [leavesByEmployeeAlias, setLeavesByEmployeeAlias] = useState<Record<string, ProjectTeamLeave[]>>({});
  const [plannedLeavesByEmployeeAlias, setPlannedLeavesByEmployeeAlias] = useState<
    Record<string, PlannedTeamLeaveType[]>
  >({});
  const [isLoadingLeaves, setIsLoadingLeaves] = useState(true);

  const fetchProjectTeamLeaves = useCallback(async () => {
    if (!projectDateRange || !project) {
      setLeavesByEmployeeAlias({});
      setPlannedLeavesByEmployeeAlias({});
      setIsLoadingLeaves(false);
      return;
    }

    setIsLoadingLeaves(true);
    try {
      const response = await sendGetRequest(`/projects/${project.id}/team/leaves`);
      const leavesJson: ProjectTeamLeave[] = await response.json();
      setLeavesByEmployeeAlias(groupLeavesByEmployeeAlias(leavesJson));
    } catch (e) {
      setLeavesByEmployeeAlias({});
    } finally {
      setIsLoadingLeaves(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjectTeamLeaves();
  }, [fetchProjectTeamLeaves]);

  useEffect(() => {
    setPlannedLeavesByEmployeeAlias(groupPlannedLeavesByEmployeeAlias(project?.teamPlannedLeaves ?? []));
  }, [project?.teamPlannedLeaves]);

  const visibleTeamMembers = useMemo(
    () => getVisibleHeatmapTeamMembers(project?.team?.members ?? []),
    [project?.team?.members],
  );

  const aliasesForNames = useMemo(() => visibleTeamMembers.map(m => m.employeeAlias), [visibleTeamMembers]);

  const namesByAlias = useEmployeeNamesByAliases(aliasesForNames);

  const leavesBlock = useMemo(() => {
    const teamMembers = (project?.team?.members ?? []) as ProjectTeamMemberDto[];
    return buildLeavesBlock(
      teamMembers,
      leavesByEmployeeAlias,
      plannedLeavesByEmployeeAlias,
      namesByAlias,
      selectedEmployeeAliases,
    );
  }, [
    project?.team?.members,
    leavesByEmployeeAlias,
    plannedLeavesByEmployeeAlias,
    namesByAlias,
    selectedEmployeeAliases,
  ]);

  return { leavesBlock, isLoadingLeaves };
}
